import {type EditTransferAllocation, type WriteTransferAllocation} from 'client';
import {type IDictionary, type InputMenuAction, inputMenuActionType, type InputMenuState} from 'interfaces';
import backendCommunicator from 'services/backendCommunicator';
import {localDateSender} from 'helpers/UnitsFormatHandler';
import Alert from 'helpers/Alert';
import {type TFunction} from 'i18next';
import {type Dispatch, type MutableRefObject} from 'react';

export const ONE_HUNDRED = 100;

export function addTransfer({
  fromSiteId,
  toSiteId,
  transferDate,
  destinationCagesPercentage,
  originCageId,
  inputMenuDispatch,
  inputMenuState,
  communicationSuccess,
  t,
  isMounted,
}: {
  fromSiteId: string;
  toSiteId: string;
  transferDate?: Date | null;
  destinationCagesPercentage?: IDictionary<number>;
  originCageId?: string | null;
  inputMenuDispatch: Dispatch<InputMenuAction> | undefined;
  inputMenuState: InputMenuState | undefined;
  communicationSuccess: (fromSite: string, toSite: string, cageIds: string[]) => void;
  t: TFunction;
  isMounted: MutableRefObject<boolean>;
}) {
  if (!transferDate || !destinationCagesPercentage || !originCageId) {
    return;
  }
  const destinationCageIds = destinationCagesPercentage && Object.keys(destinationCagesPercentage);

  const allocations: WriteTransferAllocation[] = destinationCageIds.map((cageId) => {
    const fraction = destinationCagesPercentage[cageId] && destinationCagesPercentage[cageId] / ONE_HUNDRED;
    return {
      toCageId: cageId,
      fraction,
    };
  });

  allocations &&
    backendCommunicator
      .addTransfer({
        fromSiteId,
        toSiteId,
        fromCageId: originCageId,
        date: localDateSender(transferDate),
        allocations,
      })
      .then((response) => {
        if (isMounted.current) {
          communicationSuccess(fromSiteId, toSiteId, [originCageId, ...destinationCageIds]);
          inputMenuState?.onClose && inputMenuState.onClose(response.data);
          inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
        }
      })
      .catch((error) => isMounted.current && Alert.error(error));
}

export function updateTransfer({
  transferId,
  transferDate,
  destinationCagesPercentage,
  inputMenuDispatch,
  inputMenuState,
  communicationSuccess,
  t,
  originCageId,
  fromSiteId,
  toSiteId,
  isMounted,
}: {
  transferId: string;
  transferDate?: Date | null;
  destinationCagesPercentage?: IDictionary<number>;
  inputMenuDispatch: Dispatch<InputMenuAction> | undefined;
  inputMenuState: InputMenuState | undefined;
  communicationSuccess: (fromSite: string, toSite: string, cageIds: string[]) => void;
  t: TFunction;
  originCageId: string;
  fromSiteId: string;
  toSiteId: string;
  isMounted: MutableRefObject<boolean>;
}) {
  if (!transferDate || !destinationCagesPercentage) {
    return;
  }
  const destinationCageIds = destinationCagesPercentage && Object.keys(destinationCagesPercentage);

  const allocations: EditTransferAllocation[] = destinationCageIds.map((cageId) => {
    const fraction = destinationCagesPercentage[cageId] && destinationCagesPercentage[cageId] / ONE_HUNDRED;
    return {
      toCageId: cageId,
      fraction,
    };
  });

  allocations &&
    backendCommunicator
      .updateTransfer(transferId, {
        allocations,
        date: localDateSender(transferDate),
      })
      .then((response) => {
        if (isMounted.current) {
          communicationSuccess(fromSiteId, toSiteId, [originCageId, ...destinationCageIds]);
          inputMenuState?.onClose && inputMenuState.onClose(response.data);
          inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
        }
      })
      .catch((error) => isMounted.current && Alert.error(error));
}
